/**
 * PeoplePage.jsx — CRM / Contacts page (MUI)
 *
 * Desktop: side-by-side 350px list / flex detail
 * Mobile: full-width list OR full-width detail, driven by ?select= URL param
 */

import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button, ToggleButtonGroup, ToggleButton, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useContacts } from '../hooks/useContacts';
import { useMobile } from '../hooks/useMobile';
import ContactList from '../components/crm/ContactList';
import ContactDetail from '../components/crm/ContactDetail';
import ContactForm from '../components/crm/ContactForm';
import CRMLeaderboard from '../components/crm/CRMLeaderboard';

export default function PeoplePage() {
  const { isMobile } = useMobile();
  const navigate = useNavigate();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [activeTab, setActiveTab] = useState('Contacts');
  const { data: allContacts = [] } = useContacts();
  const [searchParams, setSearchParams] = useSearchParams();

  // Mobile: ?select= stays in URL for back-button. Desktop: read once & clear.
  const selectParam = searchParams.get('select');
  const [desktopSelectedId, setDesktopSelectedId] = useState(null);

  const selectedContactId = isMobile ? selectParam : desktopSelectedId;
  const selectedContact = allContacts.find(c => c.id === selectedContactId) || null;

  // Desktop: auto-select from URL param and clear
  useEffect(() => {
    if (!isMobile && selectParam && allContacts.length > 0) {
      const found = allContacts.find((c) => c.id === selectParam);
      if (found) { setDesktopSelectedId(found.id); setIsFormOpen(false); setEditingContact(null); }
      const next = new URLSearchParams(searchParams);
      next.delete('select');
      setSearchParams(next, { replace: true });
    }
  }, [selectParam, allContacts, isMobile, searchParams, setSearchParams]);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (next.get('action') === 'newContact') {
      if (!isMobile) setDesktopSelectedId(null);
      setIsFormOpen(true);
      setEditingContact(null);
      next.delete('action');
      changed = true;
    }
    const tab = next.get('tab');
    if (tab && (tab === 'Contacts' || tab === 'Leaderboard')) { setActiveTab(tab); next.delete('tab'); changed = true; }
    if (changed) setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, isMobile]);

  const handleSelectContact = useCallback((contact) => {
    if (isMobile) {
      navigate(`/people?select=${contact.id}`);
    } else {
      setDesktopSelectedId(contact.id);
    }
    setIsFormOpen(false);
    setEditingContact(null);
  }, [isMobile, navigate]);

  const handleBack = useCallback(() => {
    if (isMobile) {
      if (isFormOpen && !selectParam) { setIsFormOpen(false); setEditingContact(null); return; }
      // Reset form state before navigation so it doesn't persist
      setIsFormOpen(false);
      setEditingContact(null);
      // Use browser back if we have history, otherwise replace to list
      if (window.history.state?.idx > 0) navigate(-1);
      else navigate('/people', { replace: true });
    } else {
      setDesktopSelectedId(null);
    }
  }, [isMobile, navigate, isFormOpen, selectParam]);

  const handleNewContact = useCallback(() => { if (!isMobile) setDesktopSelectedId(null); setIsFormOpen(true); setEditingContact(null); }, [isMobile]);
  const handleEditContact = (contact) => { setEditingContact(contact); setIsFormOpen(true); };
  const handleFormSaved = (saved) => { if (isMobile) navigate(`/people?select=${saved.id}`); else setDesktopSelectedId(saved.id); setIsFormOpen(false); setEditingContact(null); };
  const handleFormCancel = () => { setIsFormOpen(false); setEditingContact(null); };
  const handleContactDeleted = () => { if (isMobile) navigate('/people', { replace: true }); else setDesktopSelectedId(null); setIsFormOpen(false); };

  // Mobile: reset form state when URL loses ?select= (browser back)
  useEffect(() => {
    if (isMobile && !selectParam) {
      setIsFormOpen(false);
      setEditingContact(null);
    }
  }, [isMobile, selectParam]);

  // ── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    const showDetail = (selectedContact || isFormOpen) && activeTab === 'Contacts';

    if (showDetail) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={() => { handleBack(); setIsFormOpen(false); setEditingContact(null); }} sx={{ color: 'text.secondary' }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>People</Typography>
          </Box>
          {isFormOpen ? (
            <Paper sx={{ overflowY: 'auto', p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>{editingContact ? 'Edit Contact' : 'New Contact'}</Typography>
              <ContactForm key={editingContact?.id || "new"} initialContact={editingContact} onSaved={handleFormSaved} onCancel={handleFormCancel} />
            </Paper>
          ) : (
            <ContactDetail contact={selectedContact} onEdit={handleEditContact} onDeleted={handleContactDeleted} />
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>People</Typography>
          <ToggleButtonGroup size="small" value={activeTab} exclusive onChange={(_, v) => { if (v) setActiveTab(v); }}>
            <ToggleButton value="Contacts">Contacts</ToggleButton>
            <ToggleButton value="Leaderboard">Board</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {activeTab === 'Leaderboard' ? (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}><CRMLeaderboard /></Box>
        ) : (
          <ContactList selectedContactId={null} onSelect={handleSelectContact} onNewContact={handleNewContact} />
        )}
      </Box>
    );
  }

  // ── Desktop layout ───────────────────────────────────────────────────────
  return (
    <Box data-track-area="people" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Typography variant="h5" sx={{ fontFamily: '"Bebas Neue", sans-serif', letterSpacing: 1 }}>People</Typography>
        <ToggleButtonGroup size="small" value={activeTab} exclusive onChange={(_, v) => { if (v) { setActiveTab(v); if (v === 'Contacts') { setDesktopSelectedId(null); setIsFormOpen(false); } } }}>
          <ToggleButton value="Contacts">Contacts</ToggleButton>
          <ToggleButton value="Leaderboard">Leaderboard</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {activeTab === 'Leaderboard' ? (
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}><CRMLeaderboard /></Box>
      ) : (
        <Box sx={{ flex: 1, display: 'grid', gridTemplateColumns: '350px 1fr', gap: 2, minHeight: 0 }}>
          <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
            <ContactList selectedContactId={selectedContactId} onSelect={handleSelectContact} onNewContact={handleNewContact} />
          </Box>
          <Box sx={{ minHeight: 0, overflow: 'hidden' }}>
            {isFormOpen ? (
              <Paper sx={{ height: '100%', overflowY: 'auto', p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>{editingContact ? 'Edit Contact' : 'New Contact'}</Typography>
                <ContactForm key={editingContact?.id || "new"} initialContact={editingContact} onSaved={handleFormSaved} onCancel={handleFormCancel} />
              </Paper>
            ) : selectedContact ? (
              <ContactDetail contact={selectedContact} onEdit={handleEditContact} onDeleted={handleContactDeleted} />
            ) : (
              <Paper sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select a contact to view details</Typography>
                  <Button variant="contained" onClick={handleNewContact}>+ Create New Contact</Button>
                </Box>
              </Paper>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
